import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import type { IOperationResultNoValue } from '../../extViewmodels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import type { CoachingAdherenceRepScore } from '../../aida/constants/coachingAdherence';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import type { CoachingAdherenceScoreboard } from './useCoachingAdherenceScoreboardQuery';

export interface MyCoachingAdherence {
	rep: CoachingAdherenceRepScore;
	/** A call scoring at least this counts as following the coach. */
	followedScore: number;
}

const BASE_QUERY_KEY = 'aida/coffee/coaching/adherence/me';
// The judge scores a call about five minutes after the rep logs it, so polling faster buys nothing.
const REFETCH_INTERVAL_MS = 5 * 60_000;

const isClientError = (error: unknown) => (error as IOperationResultNoValue)?.systemCode >= 400;

/** Today in local time; the API's end is exclusive, so it is the next local midnight. */
export function todayRange(now: Date = new Date()): { start: string; end: string } {
	const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
	return { start: start.toISOString(), end: end.toISOString() };
}

/**
 * The signed-in user's own followed-the-coach counts for today, scored the same way as their row on the
 * managers' Adherence scoreboard. Asked for every user, sales rep or not, so the metrics show for anyone in
 * the focused view. Informational only: any failure leaves `data` empty without a toast, and a 4xx stops the poll.
 */
export function useMyCoachingAdherenceQuery() {
	const userSession = useUserSession();
	const { start, end } = todayRange();

	return useQuery({
		queryKey: [BASE_QUERY_KEY, userSession.account?.id, userSession.user?.id, start, end],
		queryFn: () =>
			userSession.webServiceHelper.callAsync<CoachingAdherenceScoreboard>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: 'aida/coffee/coaching/adherence/me',
					queryParams: { start, end },
				}),
				'GET'
			),
		select: (board): MyCoachingAdherence | null =>
			board.reps[0] ? { rep: board.reps[0], followedScore: board.followedScore } : null,
		staleTime: 60_000,
		refetchInterval: query => (isClientError(query.state.error) ? false : REFETCH_INTERVAL_MS),
		retry: (failureCount, error) => failureCount < 2 && !isClientError(error),
	});
}

export function invalidateMyCoachingAdherenceQueries() {
	return reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
}
